import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import {
  PopupNames,
  ApplicationInputType,
  Currencies,
  EmploymentTypes,
  ExistEmploymentInputType,
} from 'core';
import {
  useToasts,
  usePopupStateOperations,
  useCountriesOptions,
  useGetCoursesForApplication,
  useGetGroupsForApplication,
  useAppendTeacherApplication,
  useSortErrors,
  useAppendApplicationHelpers,
  useGetTeacherTypeNames,
  useAddTeacherExist,
} from 'core/hooks';

import {
  ToastTypes,
  ErrorsType,
  GlobalToastTypeNames,
  TeacherHireType,
} from 'types';

import { CURRENCY_OPTIONS_TEACHER } from 'components/constants';
import { DateService } from 'services/DateService';
import { AddApplicationPopupComponent } from './AddApplicationPopup';
import {
  IAddApplicationFieldsValues,
  IApplicationEmploymentValues,
} from './AddApplicationFields';
import { getApplicationSchema } from './applicationSchema';

/**
 * Details of an employment type the teacher does not have yet, in the shape
 * `addTeacherExist` expects. No `existId` — the backend then creates the type
 * and links the new application to it.
 */
const toEmploymentInput = (
  type: TeacherHireType,
  employment?: IApplicationEmploymentValues,
): ExistEmploymentInputType => {
  switch (type) {
    case TeacherHireType.Ie: {
      const values = employment?.[TeacherHireType.Ie];
      return {
        individualEntrepreneur: {
          registrationAddress: values?.registrationAddress || '',
          accountNumber: values?.accountNumber || '',
          bankTitle: values?.bankTitle || '',
          bic: values?.bic || '',
          inn: values?.inn || '',
        },
      };
    }
    case TeacherHireType.Se: {
      const values = employment?.[TeacherHireType.Se];
      return {
        selfEmployed: {
          registrationAddress: values?.registrationAddress || '',
          accountNumber: values?.accountNumber || '',
          bankTitle: values?.bankTitle || '',
          bic: values?.bic || '',
          nationality: values?.nationality?.id || '',
          passportNumber: values?.passportNumber || '',
          inn: values?.inn || '',
        },
      };
    }
    case TeacherHireType.Np: {
      const values = employment?.[TeacherHireType.Np];
      return {
        naturalPerson: {
          registrationAddress: values?.registrationAddress || '',
          accountNumber: values?.accountNumber || '',
          bankTitle: values?.bankTitle || '',
          bic: values?.bic || '',
          nationality: values?.nationality?.id || '',
          passportNumber: values?.passportNumber || '',
        },
      };
    }
    default: {
      const values = employment?.[TeacherHireType.Le];
      return {
        legalEntity: {
          registrationAddress: values?.registrationAddress || '',
          accountNumber: values?.accountNumber || '',
          bankTitle: values?.bankTitle || '',
          bic: values?.bic || '',
          tittle: values?.title || '',
          inn: values?.inn || '',
        },
      };
    }
  }
};

export const AppendApplicationPopup = memo(() => {
  const { t } = useTranslation();

  const { addToast } = useToasts();

  const { teacherId, data: existingTypes, loading } = useGetTeacherTypeNames();

  const { countries } = useCountriesOptions();
  const { courses } = useGetCoursesForApplication();
  const [getGroups, { groups }] = useGetGroupsForApplication();

  const [
    appendTeacherApplication,
    { success: appendSuccess, loading: appendLoading, errors: appendErrors },
  ] = useAppendTeacherApplication();

  const [
    addTeacherExist,
    { success: addTypeSuccess, loading: addTypeLoading, errors: addTypeErrors },
  ] = useAddTeacherExist();

  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const onSubmit = useCallback(
    (values: IAddApplicationFieldsValues) => {
      const application: ApplicationInputType = {
        title: values.applicationNumber,
        date: DateService.toISO(values.date),
        group: values.group?.value || '',
        lessonsCount: Number(values.lessonsNumber),
        country: values.servicePlace?.value,
        servicePrice: Number(values.servicePrice),
        riaPrice: values.riaPrice ? Number(values.riaPrice) : null,
        currency: Currencies[values.currency?.value as keyof typeof Currencies],
        comment: values.comment,
        teacherType:
          EmploymentTypes[
            values.teacherType?.value as keyof typeof EmploymentTypes
          ],
      };

      const pickedType = values.teacherType?.value as
        | TeacherHireType
        | undefined;

      // The teacher has no such employment type yet (typical for someone added
      // through the quick-add flow): create it together with the application
      // instead of rejecting the pick — the plain append mutation would fail
      // with INVALID_TYPE_CHOICE.
      if (pickedType && !existingTypes.includes(pickedType)) {
        addTeacherExist({
          application,
          employmentTypes: toEmploymentInput(
            pickedType,
            values.teacherTypes?.[0],
          ),
          teacherId,
        });

        return;
      }

      appendTeacherApplication({
        application,
        teacherId,
      });
    },
    [appendTeacherApplication, addTeacherExist, existingTypes, teacherId],
  );

  const {
    values,
    errors,
    handleSubmit,
    setFieldValue,
  } = useFormik<IAddApplicationFieldsValues>({
    initialValues: {
      applicationNumber: '',
      date: null,
      course: null,
      group: null,
      teacherType: null,
      lessonsNumber: '',
      servicePlace: null,
      servicePrice: '',
      riaPrice: '',
      currency: null,
      comment: '',
      teacherTypes: [
        {
          [TeacherHireType.Ie]: {},
          [TeacherHireType.Se]: {},
          [TeacherHireType.Np]: {},
          [TeacherHireType.Le]: {},
        },
      ],
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: getApplicationSchema(t),
    onSubmit,
    enableReinitialize: true,
  });

  const {
    disabledButton,
    teacherTypes,
    newEmploymentType,
    typesLoading,
    handleChange,
    onClose,
  } = useAppendApplicationHelpers({ values, setFieldValue });

  useEffect(() => {
    if (values.course && values.date) {
      getGroups({
        id: values.course.value,
        groupsDate: DateService.toISO(values.date),
      });
    }
  }, [getGroups, values.course, values.date]);

  useEffect(() => {
    if (appendSuccess || addTypeSuccess) {
      addToast({
        data: {
          title: `${t('applicationPopup.addedSuccess')}`,
          type: ToastTypes.success,
        },
        type: GlobalToastTypeNames.TeacherAgreementAdd,
      });
      hidePopup(PopupNames.appendApplicationPopup);
    }
  }, [appendSuccess, addTypeSuccess, hidePopup, addToast, t]);

  const [
    sortErrors,
    { errors: sortedErrors, message },
  ] = useSortErrors<IAddApplicationFieldsValues>();

  useEffect(() => {
    sortErrors({
      ...(errors as ErrorsType<IAddApplicationFieldsValues>),
      ...appendErrors,
      ...addTypeErrors,
    });
  }, [errors, appendErrors, addTypeErrors, sortErrors]);

  return (
    <AddApplicationPopupComponent
      values={values}
      errors={sortedErrors}
      errorsMessage={message}
      onInputChange={handleChange}
      handleSubmit={handleSubmit}
      onClose={onClose}
      loading={loading || appendLoading || addTypeLoading}
      disabledButton={disabledButton}
      groups={groups || []}
      courses={courses || []}
      teacherTypes={teacherTypes}
      newEmploymentType={newEmploymentType}
      teacherTypeDisabled={typesLoading}
      countries={countries}
      servicePlaces={countries}
      currencies={CURRENCY_OPTIONS_TEACHER}
    />
  );
});
